import React, { useId } from 'react';
import Input from './Input';
import Select from './Select';
import TextArea from './TextArea';
import './FormField.css';

const NATIVE_CONTROL_TAGS = ['input', 'select', 'textarea'];

function isFormControlElement(element) {
  if (!React.isValidElement(element)) return false;
  if (element.type === Input || element.type === Select || element.type === TextArea) return true;
  return typeof element.type === 'string' && NATIVE_CONTROL_TAGS.includes(element.type);
}

/**
 * Backward compatibility: `htmlFor`, `error`, `hint`, `required`, `className`
 * all behave exactly as before when a `htmlFor` is supplied -- the id
 * resolution below resolves to the same value (`htmlFor`) in that case, so
 * existing call sites render byte-identical output. `hint` continues to
 * work unchanged; `helperText` is a new, preferred alias for the same slot
 * (helperText wins if both are somehow passed).
 *
 * New in Phase 5:
 *   - `helperText`: alias for `hint`.
 *   - `optional`: renders a plain-text "(optional)" marker next to the
 *     label (ignored when `required` is also true). Unlike the required
 *     asterisk (a decorative glyph, hidden from assistive tech), this is
 *     real, readable words, so it is NOT aria-hidden.
 *   - `disabled`: when true and there is exactly one child, clones
 *     `disabled` onto that child (only if the child doesn't already set
 *     its own `disabled`). No existing consumer passes this prop today,
 *     so it is purely additive.
 *   - Automatic id association: if `htmlFor` is omitted, FormField uses
 *     the single child's own explicit `id` if it has one, otherwise
 *     generates a stable id via React's `useId()` and clones it onto the
 *     child. Explicit ids (via `htmlFor` or the child's own `id`) always
 *     win over the generated one.
 *   - When there is exactly one child, FormField also clones
 *     `aria-required` (from `required`) and `error` (cast to boolean, so
 *     the child's own error styling / aria-invalid activates) onto it,
 *     unless the child already sets that prop explicitly. This fixes a
 *     real, previously-shipping defect: every existing FormField+Input
 *     pairing passed `error` to FormField only, so the input itself never
 *     received the red-border/aria-invalid treatment even while its error
 *     text was showing. `aria-required` (not the native `required`
 *     attribute) is used deliberately -- it does not trigger native
 *     browser constraint-validation, so no submission/validation behavior
 *     changes for existing forms.
 *   - Multi-child usage (more than one child, or zero) is untouched: only
 *     the existing aria-describedby cloning applies, exactly as before.
 */
function FormField({
  label,
  htmlFor,
  error,
  hint,
  helperText,
  required = false,
  optional = false,
  disabled = false,
  className = '',
  children,
}) {
  const generatedId = useId();
  const resolvedHelperText = helperText !== undefined ? helperText : hint;

  // Only clone id/aria-required/disabled/error onto the single child when
  // that child is a recognized form control (ui/Input, ui/Select,
  // ui/TextArea, or a raw native <input>/<select>/<textarea>) -- never onto
  // an arbitrary wrapper element. PasswordField.js, for example, passes a
  // single child that is a <div> wrapping its own <Input> + a visibility
  // toggle button; cloning `id` onto that div would create a second element
  // sharing the Input's id (a real duplicate-id bug caught while migrating
  // this component).
  const isSingleChild = React.Children.count(children) === 1;
  const singleChild = isSingleChild ? React.Children.only(children) : null;
  const singleControlChild = isFormControlElement(singleChild) ? singleChild : null;
  const singleChildId = singleControlChild ? singleControlChild.props.id : undefined;
  const resolvedFor = htmlFor || singleChildId || generatedId;
  // `error` cloning is further narrowed to only the three ui/ wrapper
  // components below (not raw native elements): those are the only
  // elements that know what to do with a custom `error` boolean prop --
  // cloning it onto a raw <input>/<select>/<textarea> would leak an
  // unrecognized `error` attribute onto the DOM node.
  const singleWrapperChild = singleChild && (singleChild.type === Input || singleChild.type === Select || singleChild.type === TextArea)
    ? singleChild
    : null;

  const errorId = resolvedFor ? `${resolvedFor}-error` : undefined;
  const hintId  = resolvedFor ? `${resolvedFor}-hint`  : undefined;

  const childWithIds = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const extra = {};
    if (errorId && error) extra['aria-describedby'] = errorId;
    else if (hintId && resolvedHelperText) extra['aria-describedby'] = hintId;

    if (child === singleControlChild) {
      if (child.props.id === undefined) extra.id = resolvedFor;
      if (required && child.props['aria-required'] === undefined) extra['aria-required'] = true;
      if (disabled && child.props.disabled === undefined) extra.disabled = true;
    }
    if (child === singleWrapperChild) {
      if (error && child.props.error === undefined) extra.error = Boolean(error);
    }

    return React.cloneElement(child, extra);
  });

  return (
    <div className={`form-field ${className}`.trim()}>
      {label ? (
        <label className="form-field__label" htmlFor={resolvedFor}>
          {label}
          {required ? (
            <span className="form-field__required" aria-hidden="true">*</span>
          ) : optional ? (
            <span className="form-field__optional">(optional)</span>
          ) : null}
        </label>
      ) : null}
      {childWithIds}
      {resolvedHelperText && !error ? (
        <span id={hintId} className="form-field__hint">
          {resolvedHelperText}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="form-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export default FormField;
